import {colNumToExcelId} from 'bulk-import/SpreadsheetBulkImportUtil';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isStepSucceeded} from 'pipelines/PipelineStepStatusUtils';

import {CONDITION_TYPE_DISPLAY_NAMES, EdgeCondition} from './EdgeConditionTypes';

export enum EdgeConditionStatus {
  UNCHECKED = 'UNCHECKED',
  PASSED = 'PASSED',
  FAILED = 'FAILED',
}

/**
 * UI proxy — infers pass/fail from step statuses rather than a persisted evaluation result.
 * Does not resolve fan-in accurately; persistence is needed before router ships complex conditions.
 */
export function deriveEdgeConditionStatus(
  sourceStatus: PipelineStepStatusCode | null,
  targetStatus: PipelineStepStatusCode | null
): EdgeConditionStatus {
  if (sourceStatus == null || targetStatus == null) {
    return EdgeConditionStatus.UNCHECKED;
  }

  if (targetStatus === PipelineStepStatusCode.HALTED) {
    return EdgeConditionStatus.FAILED;
  }

  if (isStepSucceeded(targetStatus) || targetStatus === PipelineStepStatusCode.FAILED) {
    return EdgeConditionStatus.PASSED;
  }

  return EdgeConditionStatus.UNCHECKED;
}

export function buildConditionTooltip(condition: EdgeCondition, targetStepTypeName: string): string {
  const typeName = CONDITION_TYPE_DISPLAY_NAMES[condition.type] ?? condition.type;
  const description = typeName.charAt(0).toLowerCase() + typeName.slice(1);
  return `If ${description}, proceed to ${targetStepTypeName}`;
}

/**
 * Compute a branch label (A, B, ... Z, AA, AB...) for an edge based on its position among the
 * source step's conditioned outgoing edges. Returns '' when there is only one condition.
 */
export function computeBranchLabel(edge: {
  toStep: {id: string};
  fromStep: {outgoingEdges?: Array<{condition?: unknown; toStep: {id: string}}>};
}): string {
  const conditionedEdges = edge.fromStep.outgoingEdges?.filter((e) => e.condition != null);
  if (conditionedEdges == null || conditionedEdges.length <= 1) {
    return '';
  }
  const idx = conditionedEdges.findIndex((e) => e.toStep.id === edge.toStep.id);
  if (idx === -1) {
    return '';
  }
  return colNumToExcelId(idx);
}
